import React, { useRef, useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { motion, AnimatePresence } from 'framer-motion';

interface LottieAnimationProps {
  animationData: any;
  loop?: boolean;
  autoplay?: boolean;
  speed?: number;
  width?: string | number;
  height?: string | number;
  className?: string;
  onComplete?: () => void;
  onLoopComplete?: () => void;
  fullscreen?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
  zIndex?: number;
}

const LottieAnimation: React.FC<LottieAnimationProps> = ({
  animationData,
  loop = true,
  autoplay = true,
  speed = 1,
  width = '100%',
  height = '100%',
  className = '',
  onComplete,
  onLoopComplete,
  fullscreen = false,
  showCloseButton = false,
  onClose,
  zIndex = 9999
}) => {
  const lottieRef = useRef<any>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(speed);
    }
  }, [speed]);

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    if (!loop && fullscreen) {
      // 如果是全屏且不循环，动画完成后自动关闭
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
  };

  const handleLoopComplete = () => {
    if (onLoopComplete) {
      onLoopComplete();
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      setTimeout(() => {
        onClose();
      }, 300); // 等待退出动画完成
    }
  };

  const lottieComponent = (
    <Lottie
      lottieRef={lottieRef}
      animationData={animationData}
      loop={loop}
      autoplay={autoplay}
      style={{
        width,
        height,
      }}
      className={className}
      onComplete={handleComplete}
      onLoopComplete={handleLoopComplete}
    />
  );

  if (fullscreen) {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 flex items-center justify-center "
            style={{ zIndex }}
            onClick={showCloseButton ? undefined : handleClose}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
              className="relative w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {lottieComponent}
              {showCloseButton && (
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white text-xl font-bold transition-all duration-200"
                >
                  ×
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return lottieComponent;
};

export default LottieAnimation;